/**
 * @deprecated Use core/repositories/Back4AppAuthRepository and core/repositories/Back4AppWorkspaceRepository instead
 * This service is kept for backward compatibility but should be replaced with the new modular architecture
 */

// OPTIMIZATION: Use centralized Parse initialization
import Parse from './initializeParse'
import { profilePhotoError } from '../constants/profile'
import { staticSettingsService } from './staticSettings'
import { CompanyService } from './CompanyService'
import type { VirtualRoomKind } from '../../../types/Maps'
import { getDisplayName } from '../utils/personName'

/** The shown name for a Parse `Profile` object (SC-438): display name, else first + last, else username. */
function parseProfileName(profile: Parse.Object, fallback: string): string {
    return getDisplayName(
        {
            displayName: profile.get('displayName'),
            firstName: profile.get('firstName'),
            lastName: profile.get('lastName'),
            username: profile.get('username'),
        },
        fallback
    )
}

// Parse Classes
export const User = Parse.User
export const Query = Parse.Query
export const ParseObject = Parse.Object
export const Session = Parse.Session
export const Role = Parse.Role

// Custom Parse Object classes
export class Profile extends Parse.Object {
    constructor() {
        super('Profile')
    }

    static getInstance() {
        return Profile
    }

    get username(): string {
        return this.get('username')
    }

    set username(value: string) {
        this.set('username', value)
    }

    get email(): string {
        return this.get('email')
    }

    set email(value: string) {
        this.set('email', value)
    }

    get firstName(): string {
        return this.get('firstName')
    }

    set firstName(value: string) {
        this.set('firstName', value)
    }

    get lastName(): string {
        return this.get('lastName')
    }

    set lastName(value: string) {
        this.set('lastName', value)
    }

    get title(): string {
        return this.get('title')
    }

    set title(value: string) {
        this.set('title', value)
    }

    get contact(): string {
        return this.get('contact')
    }

    set contact(value: string) {
        this.set('contact', value)
    }

    get bio(): string {
        return this.get('bio')
    }

    set bio(value: string) {
        this.set('bio', value)
    }

    get avatar(): string {
        return this.get('avatar')
    }

    set avatar(value: string) {
        this.set('avatar', value)
    }

    get gameCharacter(): string {
        return this.get('gameCharacter')
    }

    set gameCharacter(value: string) {
        this.set('gameCharacter', value)
    }

    get customAvatarUrl(): string {
        return this.get('customAvatarUrl')
    }

    set customAvatarUrl(value: string) {
        this.set('customAvatarUrl', value)
    }

    get role(): string {
        return this.get('role')
    }

    set role(value: string) {
        this.set('role', value)
    }

    get userId(): string {
        return this.get('userId')
    }

    set userId(value: string) {
        this.set('userId', value)
    }
}

// DirectMessage Parse Object class
export class DirectMessage extends Parse.Object {
    constructor() {
        super('DirectMessage')
    }

    static getInstance() {
        return DirectMessage
    }
}

// MessageRead Parse Object class for tracking read status
export class MessageRead extends Parse.Object {
    constructor() {
        super('MessageRead')
    }

    static getInstance() {
        return MessageRead
    }

    get messageId(): string {
        return this.get('messageId')
    }

    set messageId(value: string) {
        this.set('messageId', value)
    }

    get userId(): string {
        return this.get('userId')
    }

    set userId(value: string) {
        this.set('userId', value)
    }

    get conversationId(): string {
        return this.get('conversationId')
    }

    set conversationId(value: string) {
        this.set('conversationId', value)
    }

    get readAt(): Date {
        return this.get('readAt')
    }

    set readAt(value: Date) {
        this.set('readAt', value)
    }
}

// Conversation Parse Object class
export class Conversation extends Parse.Object {
    constructor() {
        super('Conversation')
    }

    static getInstance() {
        return Conversation
    }
}

// WorkspaceSettings Parse Object class
export class WorkspaceSettings extends Parse.Object {
    constructor() {
        super('WorkspaceSettings')
    }

    static getInstance() {
        return WorkspaceSettings
    }

    get name(): string {
        return this.get('name')
    }

    set name(value: string) {
        this.set('name', value)
    }

    get logoUrl(): string {
        return this.get('logo_url')
    }

    set logoUrl(value: string) {
        this.set('logo_url', value)
    }

    get urls(): Record<string, string> {
        return this.get('urls') || {}
    }

    set urls(value: Record<string, string>) {
        this.set('urls', value)
    }

    get platformName(): string {
        return this.get('platform_name')
    }

    set platformName(value: string) {
        this.set('platform_name', value)
    }

    get platformUrl(): string {
        return this.get('platform_url')
    }

    set platformUrl(value: string) {
        this.set('platform_url', value)
    }

    get blocked_domains(): string[] {
        return this.get('blocked_domains') || []
    }

    set blocked_domains(value: string[]) {
        this.set('blocked_domains', value)
    }
}

// WorkspaceChannel Parse Object class
export class WorkspaceChannel extends Parse.Object {
    constructor() {
        super('WorkspaceChannel')
    }

    static getInstance() {
        return WorkspaceChannel
    }

    get name(): string {
        return this.get('name')
    }

    set name(value: string) {
        this.set('name', value)
    }

    get description(): string {
        return this.get('description')
    }

    set description(value: string) {
        this.set('description', value)
    }

    get isPublic(): boolean {
        return this.get('is_public')
    }

    set isPublic(value: boolean) {
        this.set('is_public', value)
    }

    get createdBy(): string {
        return this.get('created_by')
    }

    set createdBy(value: string) {
        this.set('created_by', value)
    }

    get memberCount(): number {
        return this.get('member_count') || 0
    }

    set memberCount(value: number) {
        this.set('member_count', value)
    }
}

// Channel Parse Object class
export class Channel extends Parse.Object {
    constructor() {
        super('Channel')
    }

    static getInstance() {
        return Channel
    }

    get name(): string {
        return this.get('name')
    }

    set name(value: string) {
        this.set('name', value)
    }

    get description(): string {
        return this.get('description')
    }

    set description(value: string) {
        this.set('description', value)
    }

    get type(): string {
        return this.get('type') || 'public'
    }

    set type(value: string) {
        this.set('type', value)
    }

    get isDefault(): boolean {
        return this.get('isDefault') || false
    }

    set isDefault(value: boolean) {
        this.set('isDefault', value)
    }

    get createdBy(): Parse.User {
        return this.get('createdBy')
    }

    set createdBy(value: Parse.User) {
        this.set('createdBy', value)
    }

    get createdByUserId(): string {
        return this.get('createdByUserId')
    }

    set createdByUserId(value: string) {
        this.set('createdByUserId', value)
    }

    get adminUsers(): string[] {
        return this.get('adminUsers') || []
    }

    set adminUsers(value: string[]) {
        this.set('adminUsers', value)
    }

    get moderatorUsers(): string[] {
        return this.get('moderatorUsers') || []
    }

    set moderatorUsers(value: string[]) {
        this.set('moderatorUsers', value)
    }

    get memberCount(): number {
        return this.get('memberCount') || 0
    }

    set memberCount(value: number) {
        this.set('memberCount', value)
    }

    get isPrivate(): boolean {
        return this.get('isPrivate') || false
    }

    set isPrivate(value: boolean) {
        this.set('isPrivate', value)
    }

    get settings(): Record<string, any> {
        return this.get('settings') || {}
    }

    set settings(value: Record<string, any>) {
        this.set('settings', value)
    }

    get lastMessage(): string {
        return this.get('lastMessage')
    }

    set lastMessage(value: string) {
        this.set('lastMessage', value)
    }

    get lastMessageTime(): Date {
        return this.get('lastMessageTime')
    }

    set lastMessageTime(value: Date) {
        this.set('lastMessageTime', value)
    }

    get lastMessageUser(): string {
        return this.get('lastMessageUser')
    }

    set lastMessageUser(value: string) {
        this.set('lastMessageUser', value)
    }
}

// ChannelMessage Parse Object class
export class ChannelMessage extends Parse.Object {
    constructor() {
        super('ChannelMessage')
    }

    static getInstance() {
        return ChannelMessage
    }

    get channelId(): string {
        return this.get('channelId')
    }

    set channelId(value: string) {
        this.set('channelId', value)
    }

    get senderId(): string {
        return this.get('senderId')
    }

    set senderId(value: string) {
        this.set('senderId', value)
    }

    get content(): string {
        return this.get('content')
    }

    set content(value: string) {
        this.set('content', value)
    }

    get messageType(): string {
        return this.get('messageType') || 'text'
    }

    set messageType(value: string) {
        this.set('messageType', value)
    }

    get reactions(): Array<{emoji: string, users: string[]}> {
        return this.get('reactions') || []
    }

    set reactions(value: Array<{emoji: string, users: string[]}>) {
        this.set('reactions', value)
    }

    get mentions(): string[] {
        return this.get('mentions') || []
    }

    set mentions(value: string[]) {
        this.set('mentions', value)
    }

    get isEdited(): boolean {
        return this.get('isEdited') || false
    }

    set isEdited(value: boolean) {
        this.set('isEdited', value)
    }

    get editedAt(): Date {
        return this.get('editedAt')
    }

    set editedAt(value: Date) {
        this.set('editedAt', value)
    }
}

// ChannelMembership Parse Object class
export class ChannelMembership extends Parse.Object {
    constructor() {
        super('ChannelMembership')
    }

    static getInstance() {
        return ChannelMembership
    }

    get channel(): Parse.Object {
        return this.get('channel')
    }

    set channel(value: Parse.Object) {
        this.set('channel', value)
    }

    get user(): Parse.User {
        return this.get('user')
    }

    set user(value: Parse.User) {
        this.set('user', value)
    }

    get channelId(): string {
        return this.get('channelId')
    }

    set channelId(value: string) {
        this.set('channelId', value)
    }

    get userId(): string {
        return this.get('userId')
    }

    set userId(value: string) {
        this.set('userId', value)
    }

    get role(): string {
        return this.get('role') || 'member'
    }

    set role(value: string) {
        this.set('role', value)
    }

    get joinedAt(): Date {
        return this.get('joinedAt')
    }

    set joinedAt(value: Date) {
        this.set('joinedAt', value)
    }

    get notificationSettings(): Record<string, any> {
        return this.get('notificationSettings') || {}
    }

    set notificationSettings(value: Record<string, any>) {
        this.set('notificationSettings', value)
    }

    get isMuted(): boolean {
        return this.get('isMuted') || false
    }

    set isMuted(value: boolean) {
        this.set('isMuted', value)
    }
}

// ChannelReadStatus Parse Object class
export class ChannelReadStatus extends Parse.Object {
    constructor() {
        super('ChannelReadStatus')
    }

    static getInstance() {
        return ChannelReadStatus
    }

    get channelId(): string {
        return this.get('channelId')
    }

    set channelId(value: string) {
        this.set('channelId', value)
    }

    get userId(): string {
        return this.get('userId')
    }

    set userId(value: string) {
        this.set('userId', value)
    }

    get lastReadMessageId(): string {
        return this.get('lastReadMessageId')
    }

    set lastReadMessageId(value: string) {
        this.set('lastReadMessageId', value)
    }

    get lastReadAt(): Date {
        return this.get('lastReadAt')
    }

    set lastReadAt(value: Date) {
        this.set('lastReadAt', value)
    }

    get unreadCount(): number {
        return this.get('unreadCount') || 0
    }

    set unreadCount(value: number) {
        this.set('unreadCount', value)
    }
}

// ChannelOrder Parse Object class for global admin ordering
export class ChannelOrder extends Parse.Object {
    constructor() {
        super('ChannelOrder')
    }

    static getInstance() {
        return ChannelOrder
    }

    get channelId(): string {
        return this.get('channelId')
    }

    set channelId(value: string) {
        this.set('channelId', value)
    }

    get position(): number {
        return this.get('position') || 0
    }

    set position(value: number) {
        this.set('position', value)
    }

    get isGlobal(): boolean {
        return this.get('isGlobal') || false
    }

    set isGlobal(value: boolean) {
        this.set('isGlobal', value)
    }
}

// UserChannelPreferences Parse Object class for user-specific ordering and preferences
export class UserChannelPreferences extends Parse.Object {
    constructor() {
        super('UserChannelPreferences')
    }

    static getInstance() {
        return UserChannelPreferences
    }

    get userId(): string {
        return this.get('userId')
    }

    set userId(value: string) {
        this.set('userId', value)
    }

    get channelId(): string {
        return this.get('channelId')
    }

    set channelId(value: string) {
        this.set('channelId', value)
    }

    get position(): number {
        return this.get('position') || 0
    }

    set position(value: number) {
        this.set('position', value)
    }

    get isHidden(): boolean {
        return this.get('isHidden') || false
    }

    set isHidden(value: boolean) {
        this.set('isHidden', value)
    }

    get isMuted(): boolean {
        return this.get('isMuted') || false
    }

    set isMuted(value: boolean) {
        this.set('isMuted', value)
    }
}

// Register the subclasses
Parse.Object.registerSubclass('Profile', Profile)
Parse.Object.registerSubclass('DirectMessage', DirectMessage)
Parse.Object.registerSubclass('Conversation', Conversation)
Parse.Object.registerSubclass('WorkspaceSettings', WorkspaceSettings)
Parse.Object.registerSubclass('WorkspaceChannel', WorkspaceChannel)
Parse.Object.registerSubclass('MessageRead', MessageRead)
Parse.Object.registerSubclass('Channel', Channel)
Parse.Object.registerSubclass('ChannelMessage', ChannelMessage)
Parse.Object.registerSubclass('ChannelMembership', ChannelMembership)
Parse.Object.registerSubclass('ChannelReadStatus', ChannelReadStatus)
Parse.Object.registerSubclass('ChannelOrder', ChannelOrder)
Parse.Object.registerSubclass('UserChannelPreferences', UserChannelPreferences)

// Authentication service
export const authService = {
    // Sign in with email and password
    async signInWithPassword(email: string, password: string) {
        try {
            const user = await Parse.User.logIn(email, password)
            return { data: { user }, error: null }
        } catch (error: any) {
            return { data: null, error: { message: error.message } }
        }
    },

    // Sign in with OAuth (Google)
    async signInWithOAuth(provider: string, options?: any) {
        try {
            // Parse doesn't handle OAuth the same way as Supabase
            // This would need to be implemented using Parse's OAuth adapters
            // For now, we'll return a not implemented error
            return {
                data: null,
                error: { message: 'OAuth not implemented yet. Please use email/password authentication.' }
            }
        } catch (error: any) {
            return { data: null, error: { message: error.message } }
        }
    },

    // Get current user
    getCurrentUser() {
        return Parse.User.current()
    },

    // Sign out
    async signOut() {
        try {
            await Parse.User.logOut()
            return { error: null }
        } catch (error: any) {
            return { error: { message: error.message } }
        }
    },

    // Check if user is authenticated
    isAuthenticated() {
        return Parse.User.current() !== null
    }
}

// Profile service
export const profileService = {
    // Get user profile
    async getProfile(userId: string) {
        try {
            const query = new Parse.Query(Profile)
            query.equalTo('userId', userId)
            const profile = await query.first()
            return { data: profile, error: null }
        } catch (error: any) {
            return { data: null, error: { message: error.message } }
        }
    },

    // Update user profile
    async updateProfile(userId: string, updates: any) {
        try {
            const query = new Parse.Query(Profile)
            query.equalTo('userId', userId)
            const profile = await query.first()

            if (!profile) {
                throw new Error('Profile not found')
            }

            // Update the profile with new data
            for (const key in updates) {
                if (updates.hasOwnProperty(key) && updates[key] !== undefined) {
                    profile.set(key, updates[key])
                }
            }

            const result = await profile.save()
            return { data: result, error: null }
        } catch (error: any) {
            return { data: null, error: { message: error.message } }
        }
    },

    // Create a new profile
    async createProfile(userId: string, profileData: any) {
        try {
            const profile = new Profile()
            profile.set('userId', userId)

            for (const key in profileData) {
                if (profileData.hasOwnProperty(key)) {
                    profile.set(key, profileData[key])
                }
            }

            // Set ACL so only the user can read/write their profile
            const user = await new Parse.Query(Parse.User).get(userId)
            const acl = new Parse.ACL(user)
            profile.setACL(acl)

            const result = await profile.save()
            return { data: result, error: null }
        } catch (error: any) {
            return { data: null, error: { message: error.message } }
        }
    }
}

// Direct messaging service
export const directMessageService = {
    // Find or create conversation between users
    async findOrCreateConversation(otherUserId: string) {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User must be authenticated')
            }

            const result = await Parse.Cloud.run('findOrCreateConversation', {
                otherUserId,
                otherUserName: 'Unknown User',
                otherUserAvatar: 'beam'
            })

            return { data: result, error: null }
        } catch (error: any) {
            //console.error('Error finding/creating conversation:', error)
            return { data: null, error: { message: error.message } }
        }
    },

    // Add participants to existing group conversation
    async addGroupParticipants(conversationId: string, userIds: string[]) {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User must be authenticated')
            }

            if (!userIds || userIds.length === 0) {
                throw new Error('At least one user is required')
            }

            const result = await Parse.Cloud.run('addGroupParticipants', {
                conversationId,
                newParticipantIds: userIds
            })

            return { data: result, error: null }
        } catch (error: any) {
            console.error('Error adding group participants:', error)
            return { data: null, error: { message: error.message } }
        }
    },

    // Send a direct message
    async sendMessage(conversationId: string, receiverId: string, content: string, messageType: string = 'text') {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User must be authenticated')
            }

            const params = {
              conversationId,
              receiverId,
              content,
              messageType,
            }

            console.log('🚀 Sending DM with params:', params)
            const result = await Parse.Cloud.run('sendDirectMessage', params)
            
            return { data: result, error: null }
        } catch (error: any) {
            console.error('Error sending message:', error)
            return { data: null, error: { message: error.message } }
        }
    },

    // Send a group message
    async sendGroupMessage(conversationId: string, content: string, messageType: string = 'text') {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User must be authenticated')
            }

            // Get sender name from profile
            let senderName = 'Unknown User'
            try {
                const senderProfile = await profileService.getProfile(currentUser.id)
                if (senderProfile.data) {
                    senderName = parseProfileName(senderProfile.data, currentUser.get('username') || 'Unknown User')
                }
            } catch (profileError) {
                senderName = currentUser.get('username') || 'Unknown User'
            }

            const result = await Parse.Cloud.run('sendGroupMessage', {
                conversationId,
                content,
                messageType,
                senderName
            })

            return { 
                data: {
                    id: result.messageId,
                    conversationId,
                    senderId: currentUser.id,
                    senderName,
                    content,
                    messageType,
                    createdAt: result.createdAt,
                    isRead: false,
                    isDelivered: true
                }, 
                error: null 
            }
        } catch (error: any) {
            console.error('Error sending group message:', error)
            return { data: null, error: { message: error.message } }
        }
    },

    // Get conversation messages
    async getConversationMessages(conversationId: string, limit: number = 50, skip: number = 0) {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User must be authenticated')
            }

            console.log('[getConversationMessages] Loading messages for conversation:', conversationId, 'limit:', limit, 'skip:', skip)

            const query = new Parse.Query(DirectMessage)
            query.equalTo('conversationId', conversationId)
            query.descending('createdAt')
            query.limit(limit)
            query.skip(skip)

            const messages = await query.find()
            console.log('[getConversationMessages] Found', messages.length, 'messages for conversation:', conversationId)
            
            const messageIds = messages.map(m => m.id)
            
            // Get read status for all messages
            let readStatus: Record<string, Array<{userId: string, readAt: Date}>> = {}
            if (messageIds.length > 0) {
                const readStatusResult = await this.getMessageReadStatus(messageIds, conversationId)
                if (!readStatusResult.error) {
                    readStatus = readStatusResult.data
                }
            }
            
            const formattedMessages = messages.reverse().map(message => {
                const messageReadInfo: any = readStatus[message.id]
                let isRead = false
                let readAt = null
                let readByArray: any[] = []
                
                if (messageReadInfo) {
                    if (Array.isArray(messageReadInfo)) {
                        // Fallback format: Array<{userId: string, readAt: Date}>
                        const currentUserRead = messageReadInfo.find((r: any) => r.userId === currentUser.id)
                        isRead = !!currentUserRead
                        readAt = currentUserRead?.readAt || null
                        readByArray = messageReadInfo
                    } else {
                        // Cloud function format: {messageId: string, readAt: Date, isRead: boolean}
                        isRead = messageReadInfo.isRead || false
                        readAt = messageReadInfo.readAt || null
                        readByArray = [messageReadInfo]
                    }
                }
                
                return {
                    id: message.id,
                    conversationId: message.get('conversationId'),
                    senderId: message.get('senderId'),
                    senderName: message.get('senderName') || 'Unknown User',
                    receiverId: message.get('receiverId'),
                    content: message.get('content'),
                    messageType: message.get('messageType'),
                    createdAt: message.createdAt?.getTime() || Date.now(),
                    isRead: isRead,
                    isDelivered: message.get('isDelivered'),
                    readAt: readAt,
                    deliveredAt: message.get('deliveredAt'),
                    readBy: readByArray,
                    isEdited: message.get('isEdited') || false,
                    editedAt: message.get('editedAt')
                }
            })

            return { data: formattedMessages, error: null }
        } catch (error: any) {
            //console.error('Error getting conversation messages:', error)
            return { data: [], error: { message: error.message } }
        }
    },

    // Get user conversations
    async getUserConversations(limit: number = 20, skip: number = 0) {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                // Return empty data instead of throwing an error to prevent console spam
                return { data: [], error: null }
            }

            const query = new Parse.Query(Conversation)
            query.equalTo('participantIds', currentUser.id)
            // Scope to current company if available. The cached read (5 min, shared in flight)
            // replaces a fresh cloud call that cost 4 billed requests per conversations refresh.
            try {
                const companyResult = await CompanyService.getCurrentUserCompany()
                const companyId = companyResult?.company?.id || null
                if (companyId) {
                    query.equalTo('company_id', companyId)
                }
            } catch {}
            query.descending('lastMessageTime')
            query.limit(limit)
            query.skip(skip)

            const conversations = await query.find()
            
            const formattedConversations = conversations.map(conversation => {
                const participants = conversation.get('participants') || []
                const participantIds = conversation.get('participantIds') || []
                const unreadCounts = conversation.get('unreadCounts') || {}
                const isGroup = conversation.get('isGroup') || false
                const groupName = conversation.get('groupName')
                const createdBy = conversation.get('createdBy')

                // For group conversations, don't try to find a single "other user"
                let otherUser = null
                if (!isGroup && participantIds.length === 2) {
                    // This is a direct message - find the other participant
                    const otherParticipant = participants.find((p: any) => p && p.id && p.id !== currentUser.id)
                    otherUser = otherParticipant && otherParticipant.id ? otherParticipant : null
                }

                return {
                    id: conversation.id,
                    participants: participants,
                    participantIds: participantIds,
                    lastMessage: conversation.get('lastMessage'),
                    lastMessageTime: conversation.get('lastMessageTime'),
                    unreadCount: unreadCounts[currentUser.id] || 0,
                    otherUser: otherUser,
                    isGroup: isGroup,
                    groupName: groupName,
                    createdBy: createdBy,
                    createdAt: conversation.createdAt,
                    updatedAt: conversation.updatedAt
                }
            })

            return { data: formattedConversations, error: null }
        } catch (error: any) {
            // Return empty data instead of logging error to prevent console spam when not authenticated
            return { data: [], error: null }
        }
    },

    // Mark conversation as read
    async markConversationAsRead(conversationId: string) {
        try {
            const result = await Parse.Cloud.run('markConversationAsRead', {
                conversationId
            })

            return { data: result, error: null }
        } catch (error: any) {
            //console.error('Error marking conversation as read:', error)
            return { data: null, error: { message: error.message } }
        }
    },

    // Mark a specific message as read
    async markMessageAsRead(messageId: string, conversationId?: string) {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User must be authenticated')
            }

            // Try cloud function first
            try {
                const result = await Parse.Cloud.run('markMessageAsRead', {
                    messageId,
                    conversationId
                })
                return { data: result, error: null }
            } catch (cloudError) {
                console.warn('Cloud function failed, using direct approach:', cloudError)
            }

            // Fallback to direct creation
            const readQuery = new Parse.Query(MessageRead)
            readQuery.equalTo('messageId', messageId)
            readQuery.equalTo('userId', currentUser.id)
            
            // Check if already marked as read
            const existingRead = await readQuery.first()
            if (existingRead) {
                return { data: { alreadyRead: true }, error: null }
            }

            // Create new read record
            const messageRead = new MessageRead()
            messageRead.set('messageId', messageId)
            messageRead.set('userId', currentUser.id)
            messageRead.set('readAt', new Date())
            
            if (conversationId) {
                messageRead.set('conversationId', conversationId)
            }

            // Set ACL
            const acl = new Parse.ACL()
            acl.setReadAccess(currentUser.id, true)
            acl.setWriteAccess(currentUser.id, true)
            // Allow conversation participants to read this
            if (conversationId) {
                acl.setPublicReadAccess(true) // Will be filtered by conversation participation
            }
            messageRead.setACL(acl)

            await messageRead.save()

            return { data: { marked: true }, error: null }
        } catch (error: any) {
            console.error('Error marking message as read:', error)
            return { data: null, error: { message: error.message } }
        }
    },

    // Mark multiple messages as read (bulk operation)
    async markMessagesAsRead(messageIds: string[], conversationId?: string) {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User must be authenticated')
            }

            // Try cloud function first for better performance
            try {
                const result = await Parse.Cloud.run('markMessagesAsRead', {
                    messageIds,
                    conversationId
                })
                return { data: result, error: null }
            } catch (cloudError) {
                console.warn('Bulk cloud function failed, falling back to individual operations:', cloudError)
            }

            // Fallback: mark each message individually
            const results = []
            for (const messageId of messageIds) {
                const result = await this.markMessageAsRead(messageId, conversationId)
                results.push(result)
            }

            return { data: { results }, error: null }
        } catch (error: any) {
            console.error('Error bulk marking messages as read:', error)
            return { data: null, error: { message: error.message } }
        }
    },

    // Get read status for messages
    async getMessageReadStatus(messageIds: string[], conversationId?: string) {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User must be authenticated')
            }

            // Try cloud function first for better performance
            try {
                const result = await Parse.Cloud.run('getMessageReadStatus', {
                    messageIds,
                    conversationId
                })
                return { data: result, error: null }
            } catch (cloudError) {
                console.warn('Read status cloud function failed, using direct query:', cloudError)
            }

            // Fallback to direct query
            const readQuery = new Parse.Query(MessageRead)
            readQuery.containedIn('messageId', messageIds)
            
            if (conversationId) {
                readQuery.equalTo('conversationId', conversationId)
            }

            const readRecords = await readQuery.find()
            
            // Group by message ID
            const readStatus: Record<string, Array<{userId: string, readAt: Date}>> = {}
            messageIds.forEach(id => {
                readStatus[id] = []
            })
            
            readRecords.forEach(record => {
                const messageId = record.get('messageId')
                const userId = record.get('userId')
                const readAt = record.get('readAt')
                
                if (readStatus[messageId]) {
                    readStatus[messageId].push({ userId, readAt })
                }
            })

            return { data: readStatus, error: null }
        } catch (error: any) {
            console.error('Error getting message read status:', error)
            return { data: {}, error: { message: error.message } }
        }
    },

    // Get conversation read status for current user
    async getConversationReadStatus(conversationId: string) {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User must be authenticated')
            }

            // Get all messages in conversation
            const messageQuery = new Parse.Query(DirectMessage)
            messageQuery.equalTo('conversationId', conversationId)
            messageQuery.select(['objectId'])
            const messages = await messageQuery.find()
            
            const messageIds = messages.map(msg => msg.id)
            
            if (messageIds.length === 0) {
                return { data: {}, error: null }
            }

            // Get read status for these messages
            const readQuery = new Parse.Query(MessageRead)
            readQuery.containedIn('messageId', messageIds)
            readQuery.equalTo('userId', currentUser.id)
            
            const readRecords = await readQuery.find()
            const readMessageIds = new Set(readRecords.map(r => r.get('messageId')))
            
            // Create status map
            const status: Record<string, boolean> = {}
            messageIds.forEach(id => {
                status[id] = readMessageIds.has(id)
            })

            return { data: status, error: null }
        } catch (error: any) {
            console.error('Error getting conversation read status:', error)
            return { data: {}, error: { message: error.message } }
        }
    },

    // Get unread message count
    async getUnreadMessageCount(retryCount = 0, baseDelay = 1000) {
        try {
            const result = await Parse.Cloud.run('getUnreadMessageCount')
            return { data: result, error: null }
        } catch (error: any) {
            // If it's a 502 error and we haven't retried too many times
            if (error.code === 502 && retryCount < 3) {
                // Exponential backoff delay
                const delay = baseDelay * Math.pow(2, retryCount)
                await new Promise(resolve => setTimeout(resolve, delay))
                return this.getUnreadMessageCount(retryCount + 1, baseDelay)
            }
            
            //console.error('Error getting unread message count:', error)
            return { data: { unreadCount: 0 }, error: { message: error.message } }
        }
    },

    // Test function to check conversation messages
    async testConversationMessages(conversationId: string) {
        try {
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User must be authenticated')
            }

            console.log('[testConversationMessages] Testing messages for conversation:', conversationId)

            const query = new Parse.Query(DirectMessage)
            query.equalTo('conversationId', conversationId)
            query.descending('createdAt')
            query.limit(10)

            const messages = await query.find()
            console.log('[testConversationMessages] Found', messages.length, 'messages for conversation:', conversationId)
            
            const messageDetails = messages.map(message => ({
                id: message.id,
                senderId: message.get('senderId'),
                senderName: message.get('senderName'),
                content: message.get('content'),
                createdAt: message.createdAt,
                isRead: message.get('isRead'),
                isGroup: message.get('isGroup')
            }))

            return { 
                data: messageDetails, 
                error: null,
                messageCount: messages.length
            }
        } catch (error: any) {
            console.error('[testConversationMessages] Error:', error)
            return { data: [], error: { message: error.message } }
        }
    },

    // Refresh participant data for existing conversations
    async refreshConversationParticipants(conversationId: string) {
        try {
            const result = await Parse.Cloud.run('refreshConversationParticipants', {
                conversationId
            })
            return { data: result, error: null }
        } catch (error: any) {
            console.error('Error refreshing conversation participants:', error)
            return { data: null, error: { message: error.message } }
        }
    },
}

// File upload service
export const fileService = {
    // Upload a profile photo (the only file this service stores)
    async uploadFile(file: File, fileName?: string) {
        try {
            const invalid = profilePhotoError(file)
            if (invalid) {
                return { data: null, error: { message: invalid } }
            }
            const name = fileName || file.name
            const parseFile = new Parse.File(name, file)
            const result = await parseFile.save()

            return {
                data: {
                    publicUrl: result.url(),
                    path: result.name()
                },
                error: null
            }
        } catch (error: any) {
            return { data: null, error: { message: error.message } }
        }
    }
}

// Workspace service
export const workspaceService = {
    /**
     * Cached settings so we do not hit the backend repeatedly.
     * The cache intentionally lives for the entire runtime of the tab. If admins
     * change the workspace configuration we already have an explicit
     * updateSettings call below that refreshes this cache.
     */
    _cachedSettings: null as WorkspaceSettings | null,

    // Get workspace settings
    async getSettings() {
        // Return cached result if we already have it
        if (this._cachedSettings) {
            return this._cachedSettings
        }

        const fetchSettings = async () => {
            // First try to get settings using the public cloud function
            try {
                const response = await Parse.Cloud.run('getPublicWorkspaceSettings')
                if (response.success && response.settings) {
                    this._cachedSettings = response.settings
                    return response.settings
                }
            } catch (publicError: any) {
                // Check if it's a "function not found" error
                if (publicError.message?.includes('Invalid function') || publicError.code === 141) {
                    const staticSettings = await staticSettingsService.loadStaticSettings()
                    this._cachedSettings = staticSettings
                    return staticSettings
                }
            }

            // Fallback to authenticated method if public fails
            const query = new Parse.Query(WorkspaceSettings)
            const settings = await query.first()

            if (!settings) {
                return null
            }

            // Convert Parse object to plain interface
            const formatted = {
                id: settings.id,
                name: settings.get('name'),
                logo_url: settings.get('logo_url'),
                urls: settings.get('urls') || {},
                platform_name: settings.get('platform_name'),
                platform_url: settings.get('platform_url'),
                blocked_domains: settings.get('blocked_domains') || [],
                registration_enabled: settings.get('registration_enabled') !== false,
                features: settings.get('features') || undefined,
                restrictions: settings.get('restrictions') || undefined,
                appearance_config: settings.get('appearance_config') || undefined,
                theme_tokens: settings.get('theme_tokens') || undefined,
                branding_extension: settings.get('branding_extension') || undefined,
                waitlist_url: settings.get('waitlist_url') || undefined,
                created_at: settings.createdAt?.toISOString() || '',
                updated_at: settings.updatedAt?.toISOString() || ''
            }

            this._cachedSettings = formatted
            return formatted
        }

        try {
            return await fetchSettings()
        } catch (error: any) {
            // If the failure was due to an invalid / expired session token we clear it once and retry anonymously
            if (error.code === Parse.Error.INVALID_SESSION_TOKEN || error.code === Parse.Error.SESSION_MISSING) {
                try {
                    await Parse.User.logOut()
                } catch (logoutErr) {
                    // Ignore logout errors
                }

                try {
                    return await fetchSettings()
                } catch (retryError) {
                    // Final fallback to static settings
                    return await staticSettingsService.loadStaticSettings()
                }
            }

            // Final fallback to static settings
            return await staticSettingsService.loadStaticSettings()
        }
    },

    // Update workspace settings (legacy positional params)
    async updateSettings(
        name: string,
        logo_url?: string,
        urls: Record<string, string> = {},
        platform_name?: string,
        platform_url?: string,
        blocked_domains: string[] = []
    ) {
        try {
            // Check if user is admin
            const currentUser = Parse.User.current()
            if (!currentUser) {
                throw new Error('User not authenticated')
            }

            // Get user profile to check role
            const profileQuery = new Parse.Query(Profile)
            profileQuery.equalTo('userId', currentUser.id)
            const profile = await profileQuery.first()

            if (!profile || profile.get('role') !== 'admin') {
                throw new Error('Unauthorized: Only admins can manage workspace settings')
            }

            // Try to get existing settings first
            const query = new Parse.Query(WorkspaceSettings)
            let settings = await query.first()

            if (!settings) {
                // Create new settings if none exist
                settings = new WorkspaceSettings()
            }

            // Update the settings
            settings.set('name', name)
            if (logo_url !== undefined) settings.set('logo_url', logo_url)
            settings.set('urls', urls)
            if (platform_name !== undefined) settings.set('platform_name', platform_name)
            if (platform_url !== undefined) settings.set('platform_url', platform_url)
            settings.set('blocked_domains', blocked_domains)

            const result = await settings.save()

            // Return in interface format
            return {
                id: result.id,
                name: result.get('name'),
                logo_url: result.get('logo_url'),
                urls: result.get('urls') || {},
                platform_name: result.get('platform_name'),
                platform_url: result.get('platform_url'),
                blocked_domains: result.get('blocked_domains') || [],
                registration_enabled: result.get('registration_enabled') !== false,
                created_at: result.createdAt?.toISOString() || '',
                updated_at: result.updatedAt?.toISOString() || ''
            }
        } catch (error: any) {
            //console.error('Error updating workspace settings:', error)
            return null
        }
    },

    // Update workspace settings via object (preferred)
    async updateSettingsObject(settingsObj: Partial<import('../core/models/Workspace').WorkspaceSettings>) {
        const currentUser = Parse.User.current()
        if (!currentUser) {
            throw new Error('User not authenticated')
        }

        const profileQuery = new Parse.Query(Profile)
        profileQuery.equalTo('userId', currentUser.id)
        const profile = await profileQuery.first()
        if (!profile || profile.get('role') !== 'admin') {
            throw new Error('Unauthorized: Only admins can manage workspace settings')
        }

        // Use cloud function to ensure master key and schema compliance
        const response = await Parse.Cloud.run('updateWorkspaceSettings', { settings: settingsObj })
        if (!response || response.success !== true) {
            throw new Error('Failed to update workspace settings')
        }
        const result = response.settings
        const formatted = {
            id: result.id,
            name: result.name,
            logo_url: result.logo_url,
            urls: result.urls || {},
            platform_name: result.platform_name,
            platform_url: result.platform_url,
            blocked_domains: result.blocked_domains || [],
            registration_enabled: result.registration_enabled !== false,
            features: result.features,
            restrictions: result.restrictions,
            appearance_config: result.appearance_config,
            theme_tokens: result.theme_tokens,
            branding_extension: result.branding_extension,
            waitlist_url: result.waitlist_url,
            created_at: result.created_at?.toISOString ? result.created_at.toISOString() : (result.created_at || ''),
            updated_at: result.updated_at?.toISOString ? result.updated_at.toISOString() : (result.updated_at || '')
        }
        this._cachedSettings = formatted as import('../core/models/Workspace').WorkspaceSettings
        return formatted
    },

    // Get workspace channels
    async getChannels() {
        try {
            const query = new Parse.Query(WorkspaceChannel)
            query.ascending('name')
            const channels = await query.find()

            return channels.map(channel => ({
                id: channel.id,
                name: channel.get('name'),
                description: channel.get('description'),
                is_public: channel.get('is_public'),
                created_at: channel.createdAt?.toISOString() || '',
                updated_at: channel.updatedAt?.toISOString() || '',
                created_by: channel.get('created_by'),
                member_count: channel.get('member_count') || 0
            }))
        } catch (error: any) {
            //console.error('Error fetching workspace channels:', error)
            return []
        }
    }
}

export class VirtualRoom extends Parse.Object {
    constructor() {
        super('VirtualRoom')
    }

    static getInstance() {
        return VirtualRoom
    }

    get mapId(): string {
        return this.get('mapId')
    }

    set mapId(value: string) {
        this.set('mapId', value)
    }

    get virtualRoomId(): string {
        return this.get('virtualRoomId')
    }

    set virtualRoomId(value: string) {
        this.set('virtualRoomId', value)
    }

    get kind(): VirtualRoomKind {
        return this.get('kind')
    }

    set kind(value: VirtualRoomKind) {
        this.set('kind', value)
    }

    get name(): string {
        return this.get('name')
    }

    set name(value: string) {
        this.set('name', value)
    }

    get color(): string {
        return this.get('color')
    }

    set color(value: string) {
        this.set('color', value)
    }

    get properties(): {
        uniqueRoomId: string
        proximityDistance: number
    } {
        return this.get('properties')
    }

    set properties(value: {
        uniqueRoomId: string
        proximityDistance: number
    }) {
        this.set('properties', value)
    }

    get bounds(): {
        x: number
        y: number
        width: number
        height: number
    } | undefined {
        return this.get('bounds')
    }

    set bounds(value: {
        x: number
        y: number
        width: number
        height: number
    } | undefined) {
        this.set('bounds', value)
    }

    get metadata(): {
        description?: string
        roomType?: string
        capacity?: number
    } | undefined {
        return this.get('metadata')
    }

    set metadata(value: {
        description?: string
        roomType?: string
        capacity?: number
    } | undefined) {
        this.set('metadata', value)
    }

    get createdBy(): string {
        return this.get('createdBy')
    }

    set createdBy(value: string) {
        this.set('createdBy', value)
    }

    get lastModifiedBy(): string {
        return this.get('lastModifiedBy')
    }

    set lastModifiedBy(value: string) {
        this.set('lastModifiedBy', value)
    }

    get version(): number {
        return this.get('version') || 1
    }

    set version(value: number) {
        this.set('version', value)
    }
}

export class VirtualRoomTile extends Parse.Object {
    constructor() {
        super('VirtualRoomTile')
    }

    static getInstance() {
        return VirtualRoomTile
    }

    get mapId(): string {
        return this.get('mapId')
    }

    set mapId(value: string) {
        this.set('mapId', value)
    }

    get virtualRoomId(): string {
        return this.get('virtualRoomId')
    }

    set virtualRoomId(value: string) {
        this.set('virtualRoomId', value)
    }

    get tileX(): number {
        return this.get('tileX')
    }

    set tileX(value: number) {
        this.set('tileX', value)
    }

    get tileY(): number {
        return this.get('tileY')
    }

    set tileY(value: number) {
        this.set('tileY', value)
    }

    get tileType(): 'room' | 'outline' {
        return this.get('tileType')
    }

    set tileType(value: 'room' | 'outline') {
        this.set('tileType', value)
    }

    get createdBy(): string {
        return this.get('createdBy')
    }

    set createdBy(value: string) {
        this.set('createdBy', value)
    }

    get lastModifiedBy(): string {
        return this.get('lastModifiedBy')
    }

    set lastModifiedBy(value: string) {
        this.set('lastModifiedBy', value)
    }

    get version(): number {
        return this.get('version') || 1
    }

    set version(value: number) {
        this.set('version', value)
    }
}

// Create the main back4app service object that includes all services
const back4appService = {
  authService,
  profileService,
  directMessageService,
  fileService,
  workspaceService,
}

export default back4appService